import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch, useSelector } from 'react-redux';

import {
    cargarDetalleDevolucionFactura,
    limpiarDetalleDevolucionFactura,
    devolverFacturaParcial
} from '../../../actions/facturas';

const toNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '$0.00';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
};

const DevolverFacturaModal = ({ show, factura, onCancel, onConfirm, onSuccess }) => {
    const dispatch = useDispatch();
    const detalleDevolucion = useSelector(state => state.facturas?.detalle_devolucion);

    const [tipoDevolucion, setTipoDevolucion] = useState('completa');
    const [rows, setRows] = useState([]);
    const [montoReal, setMontoReal] = useState('');
    const [montoSugerido, setMontoSugerido] = useState(0);
    const [montoEditado, setMontoEditado] = useState(false);
    const [cargando, setCargando] = useState(false);

    const facturaInfo = detalleDevolucion || null;

    useEffect(() => {
        if (!show) return;
        setTipoDevolucion('completa');
        setRows([]);
        setMontoReal('');
        setMontoSugerido(0);
        setMontoEditado(false);
        setCargando(false);
        dispatch(limpiarDetalleDevolucionFactura());
    }, [show, dispatch]);

    useEffect(() => {
        if (!show) return;
        if (tipoDevolucion !== 'parcial') return;
        if (!factura?._id) return;

        const cargar = async () => {
            setCargando(true);
            await dispatch(cargarDetalleDevolucionFactura(factura._id));
            setCargando(false);
        };

        cargar();
    }, [show, tipoDevolucion, factura, dispatch]);

    const calcularTotalDesdeRows = (rws) => {
        const agrupado = {};

        for (let i = 0; i < rws.length; i++) {
            const r = rws[i];
            if (!agrupado[r.producto_id]) {
                agrupado[r.producto_id] = {
                    precio_unitario: toNum(r.precio_unitario),
                    total: 0
                };
            }
            agrupado[r.producto_id].total += toNum(r.cantidad);
        }

        let total = 0;
        const keys = Object.keys(agrupado);
        for (let i = 0; i < keys.length; i++) {
            const k = keys[i];
            total += (toNum(agrupado[k].total) * toNum(agrupado[k].precio_unitario));
        }

        return toNum(total);
    };

    useEffect(() => {
        if (!show) return;
        if (tipoDevolucion !== 'parcial') return;
        if (!facturaInfo?._id) return;

        const productos = Array.isArray(facturaInfo?.productos) ? facturaInfo.productos : [];

        const nuevasRows = [];
        for (let i = 0; i < productos.length; i++) {
            const p = productos[i];
            const movimientos = Array.isArray(p?.movimientos) ? p.movimientos : [];
            const cantidadTotal = toNum(p?.cantidad_surtir);

            /* Sin movimientos registrados = nada entró a inventario de este producto para esta
               factura (aunque cantidad_surtir sea > 0, pudo no haberse recibido aún). No hay nada
               físico que revertir, así que no se genera fila — antes se ofrecía devolver la
               cantidad facturada completa con ubicación en blanco, lo cual no correspondía a
               ningún inventario real. */
            if (!movimientos.length) continue;

            for (let j = 0; j < movimientos.length; j++) {
                const m = movimientos[j];
                const cantidadMov = toNum(m?.cantidad);

                nuevasRows.push({
                    row_id: `${p?._id || i}-${j}`,
                    producto_id: p?._id || '',
                    producto: p?.nombre || 'Producto',
                    precio_unitario: toNum(p?.precio_unitario),
                    unidad: p?.unidad?.nombre || 'Unidad',
                    producto_cantidad_max_total: cantidadTotal,
                    cantidad_max: cantidadMov,
                    area: { _id: m?.area?._id || '', nombre: m?.area?.nombre || '' },
                    organizador: { _id: m?.organizador?._id || '', nombre: m?.organizador?.nombre || '' },
                    ubicacion: { _id: m?.ubicacion?._id || '', nombre: m?.ubicacion?.nombre || '' },
                    cantidad: cantidadMov
                });
            }
        }

        setRows(nuevasRows);

        const totalCalc = calcularTotalDesdeRows(nuevasRows);
        setMontoSugerido(totalCalc);

        const base = facturaInfo?.monto_calculado ?? facturaInfo?.monto_real ?? facturaInfo?.monto ?? totalCalc;
        setMontoReal(String(toNum(base)));
        setMontoEditado(false);
    }, [show, tipoDevolucion, facturaInfo]);

    const onChangeCantidad = (rowId, value) => {
        const v = toNum(value);

        setRows(prev => {
            const next = prev.map(r => ({ ...r }));
            const idx = next.findIndex(r => r.row_id === rowId);
            if (idx === -1) return prev;

            const row = next[idx];
            const maxFila = toNum(row.cantidad_max);
            const maxTotal = toNum(row.producto_cantidad_max_total);

            let sumaOtros = 0;
            for (let i = 0; i < next.length; i++) {
                if (next[i].producto_id === row.producto_id && next[i].row_id !== rowId) {
                    sumaOtros += toNum(next[i].cantidad);
                }
            }

            const maxPermitidoTotal = Math.max(0, maxTotal - sumaOtros);
            const maxPermitido = Math.min(maxFila, maxPermitidoTotal);

            const nueva = Math.min(Math.max(0, v), maxPermitido);
            next[idx].cantidad = nueva;

            const totalCalc = calcularTotalDesdeRows(next);
            setMontoSugerido(totalCalc);

            if (!montoEditado) {
                setMontoReal(String(toNum(totalCalc)));
            }

            return next;
        });
    };

    const productosPayload = useMemo(() => {
        const map = {};

        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!map[r.producto_id]) {
                map[r.producto_id] = {
                    _id: r.producto_id,
                    ubicaciones: []
                };
            }

            map[r.producto_id].ubicaciones.push({
                area: r?.area?._id || '',
                organizador: r?.organizador?._id || '',
                ubicacion: r?.ubicacion?._id || '',
                cantidad: toNum(r?.cantidad)
            });
        }

        return Object.values(map);
    }, [rows]);

    const onConfirmParcial = async () => {
        if (!factura?._id) return;

        const payload = {
            id: factura._id,
            id_metodo: null,
            monto_real: toNum(montoReal),
            productos: productosPayload
        };

        const ok = await dispatch(devolverFacturaParcial(payload));
        if (ok) {
            dispatch(limpiarDetalleDevolucionFactura());
            if (onSuccess) await onSuccess();
        }
    };

    const cerrar = () => {
        dispatch(limpiarDetalleDevolucionFactura());
        onCancel();
    };

    return (
        <Fragment>
            <Modal
                show={show}
                onHide={cerrar}
                centered
                backdrop="static"
                size={tipoDevolucion === 'parcial' ? 'xl' : undefined}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Devolución factura - {factura?.numero || ''}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3">
                        <select
                            className="form-select"
                            value={tipoDevolucion}
                            onChange={(e) => setTipoDevolucion(e.target.value)}
                        >
                            <option value="completa">Devolución completa</option>
                            <option value="parcial">Devolución parcial</option>
                        </select>
                    </div>

                    {tipoDevolucion === 'completa' && (
                        <div className="alert alert-warning mb-0">
                            ¿Seguro que deseas devolver la factura <b>{factura?.numero || ''}</b> y sus productos?
                            <br />
                            Esta acción <b>no tiene vuelta atrás</b> y afectará el inventario.
                        </div>
                    )}

                    {tipoDevolucion === 'parcial' && (
                        <Fragment>
                            <div className="alert alert-warning">
                                <b>Importante:</b> al realizar una <b>devolución parcial</b> se modifica el inventario y esta acción no es reversible.
                            </div>

                            {cargando && (
                                <div className="alert alert-info mb-0">
                                    Cargando detalles de la factura...
                                </div>
                            )}

                            {!cargando && facturaInfo && (
                                <Fragment>
                                    <div className="mb-3">
                                        <div className="fw-bold">
                                            {facturaInfo?.proveedor?.nombre || 'Proveedor'}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: 13 }}>
                                            #{facturaInfo?.numero || 'Sin número'} · {formatDate(facturaInfo?.fecha)}
                                        </div>
                                    </div>

                                    <div className="row g-2 mb-3">
                                        <div className="col-12 col-md-6">
                                            <div className="form-floating">
                                                <CurrencyInput
                                                    id="monto_real_devolucion"
                                                    className="form-control"
                                                    value={montoReal}
                                                    decimalsLimit={3}
                                                    allowNegativeValue={false}
                                                    placeholder="Nuevo monto"
                                                    onValueChange={(val) => {
                                                        setMontoReal(val || '');
                                                        setMontoEditado(true);
                                                    }}
                                                />
                                                <label htmlFor="monto_real_devolucion">Nuevo monto</label>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 d-flex align-items-center">
                                            <div className="text-muted" style={{ fontSize: 13 }}>
                                                Monto recalculado sugerido: <b>{formatMoney(montoSugerido)}</b>
                                            </div>
                                        </div>
                                    </div>

                                    <table
                                        className="table table-bordered table-sm align-middle"
                                        style={{ fontSize: 12, tableLayout: 'fixed', width: '100%' }}
                                    >
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '32%' }}>Producto</th>
                                                <th style={{ width: '12%' }}>Área</th>
                                                <th style={{ width: '14%' }}>Organizador</th>
                                                <th style={{ width: '14%' }}>Ubicación</th>
                                                <th style={{ width: '10%' }} className="text-end">Precio</th>
                                                <th style={{ width: '10%' }} className="text-center">Cantidad</th>
                                                <th style={{ width: '8%' }} className="text-center">Unidad</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(r => (
                                                <tr key={r.row_id}>
                                                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {r.producto}
                                                    </td>
                                                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {r?.area?.nombre || '-'}
                                                    </td>
                                                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {r?.organizador?.nombre || '-'}
                                                    </td>
                                                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {r?.ubicacion?.nombre || '-'}
                                                    </td>
                                                    <td className="text-end">
                                                        {formatMoney(r.precio_unitario)}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center"
                                                            value={r.cantidad}
                                                            min={0}
                                                            max={r.cantidad_max}
                                                            step="1"
                                                            onChange={(e) => onChangeCantidad(r.row_id, e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        {r.unidad || 'Unidad'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Fragment>
                            )}
                        </Fragment>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={cerrar}>
                        Cerrar
                    </button>

                    {tipoDevolucion === 'completa' && (
                        <button type="button" className="btn btn-warning" onClick={onConfirm}>
                            Confirmar devolución
                        </button>
                    )}

                    {tipoDevolucion === 'parcial' && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={onConfirmParcial}
                            disabled={cargando}
                        >
                            Confirmar devolución
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default DevolverFacturaModal;
